import moment from "moment-timezone";
import Form from "rc-field-form";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/Button";
import Field from "../../../components/Field";
import DateInput from "../../../components/Inputs/DateInput";
import ResourceSelect from "../../../components/Inputs/ResourceSelect";
import {
    getSettingsResourceSearchFiltersWithLookups,
    renderSettingsResourceLabel,
} from "../../../utils/helpers/settings";

const getWorkingShiftSearchFilters = (search) => {
    if (!search) {
        return;
    }
    return getSettingsResourceSearchFiltersWithLookups(search);
};

function WorkingShiftsForm({ mode, values, close, create, edit, loading }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const isEdit = mode === "edit";

    const onFinish = useCallback(
        (data) => {
            const formattedData = {
                workingShifts: data.workingShifts?.map((item) => item.id) || [],
                startEffectiveDate: moment(data.startEffectiveDate).format("YYYY-MM-DD"),
                endEffectiveDate: data.endEffectiveDate
                    ? moment(data.endEffectiveDate).format("YYYY-MM-DD")
                    : null,
                override: false,
            };
            if (isEdit && values?.id) {
                edit(values.id, formattedData, close);
            } else {
                create(formattedData, close);
            }
        },
        [isEdit, values?.id, edit, create, close]
    );

    useEffect(() => {
        form.setFieldsValue({
            workingShifts: values?.workingShifts || [],
            startEffectiveDate: values?.startEffectiveDate
                ? moment(values.startEffectiveDate).toDate()
                : undefined,
            endEffectiveDate: values?.endEffectiveDate
                ? moment(values.endEffectiveDate).toDate()
                : undefined,
        });
    }, [form, values]);

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
        >
            <Field
                name="startEffectiveDate"
                label={t("start-effective-date")}
                rules={[
                    {
                        required: true,
                        message: t("required-start-effective-date"),
                    },
                ]}
            >
                <DateInput placeholder={t("start-effective-date")} />
            </Field>

            <Field
                name="endEffectiveDate"
                label={t("end-effective-date")}
                dependencies={["startEffectiveDate"]}
                rules={[
                    ({ getFieldValue }) => ({
                        validator: (_, value) => {
                            const startDate = getFieldValue("startEffectiveDate");
                            if (!startDate || !value) {
                                return Promise.resolve();
                            }
                            if (moment(value).isSameOrBefore(moment(startDate), "date")) {
                                return Promise.reject(new Error(t("end-date-bigger-than-start-date")));
                            }
                            return Promise.resolve();
                        },
                    }),
                ]}
            >
                <DateInput placeholder={t("end-effective-date")} />
            </Field>

            <Field
                name="workingShifts"
                label={t("working-shift")}
                rules={[
                    {
                        required: true,
                        message: t("required-working-shift"),
                    },
                ]}
            >
                <ResourceSelect
                    resourcePath="/working-shift"
                    renderLabel={renderSettingsResourceLabel}
                    getSearchFilters={getWorkingShiftSearchFilters}
                    placeholder={t("working-shift")}
                    mode="multiple"
                    hasSearch
                />
            </Field>

            <div className="d-flex justify-content-end">
                <Button
                    type="button"
                    className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
                    onClick={close}
                    disabled={loading}
                >
                    {t("cancel")}
                </Button>
                <Button
                    type="submit"
                    className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
                    loading={loading}
                >
                    {t("save")}
                </Button>
            </div>
        </Form>
    );
}

export default WorkingShiftsForm;
